import { describeCreatedBy, describeLastUpdate } from '../../core/audit-stamp';
import type { AuditStamped } from '../../core/audit-stamp';

interface AuditStampProps {
  /** The record itself — the line is read off its own two names and stamps. */
  record: AuditStamped | null | undefined;
  /** Evaluation moment, so "today" never depends on when the card re-rendered. */
  now?: number;
  /**
   * Show the author instead of the last editor. The roster wants "who last
   * moved this"; a card that has room for both shows the creation too.
   */
  mode?: 'updated' | 'created';
  className?: string;
}

/**
 * `עודכן ע״י אשר אמר · 16:05` — the quiet line that says who last touched a
 * record, without opening it.
 *
 * Deliberately subtle: it sits under the row in the smallest size the design
 * uses and at 45% ink, because it is a footnote on every card and would shout
 * over the operational content if it were not. The rows it names are the ones
 * the unit actually changes hourly — a task on the board, a vehicle's status,
 * a soldier's whereabouts.
 *
 * A record that carries no name renders nothing at all. An unsigned "עודכן ע״י"
 * would be worse than silence, and a row written before the field existed is
 * still a perfectly good row.
 *
 * The sentence itself is composed in `src/core/audit-stamp.ts` and nowhere else,
 * so every screen says it the same way and the wording is changed in one place.
 */
export function AuditStamp({ record, now, mode = 'updated', className }: AuditStampProps) {
  const line = mode === 'created' ? describeCreatedBy(record, now) : describeLastUpdate(record, now);
  if (!line) return null;

  return (
    <p
      data-audit-stamp={mode}
      title={line}
      className={['text-[11px] leading-tight text-ink/45 truncate', className]
        .filter(Boolean)
        .join(' ')}
    >
      {line}
    </p>
  );
}
